import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/outline";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Workflow, WorkflowAutomationItem } from "../interfaces";
import { getItemTitle } from "../utils";
import UsagePanel from "./UsagePanel";

interface Props {
  currentItem: WorkflowAutomationItem;
  workflow: Workflow;
  prevItemId?: string;
  nextItemId?: string;
  sessionPosition?: number;
  sessionTotal?: number;
  onNavigate: (itemId: string) => void;
  workflowId?: string;
  isActionRequired?: boolean;
}

export default function ItemDetailHeader({ currentItem, workflow, prevItemId, nextItemId, sessionPosition, sessionTotal, onNavigate, workflowId, isActionRequired }: Props) {
  const history = useHistory();
  const { t } = useTranslation();

  const hasSession = sessionPosition !== undefined && sessionTotal !== undefined;

  // Breadcrumb back destination: the workflow's outputs, or the global outputs page
  const backPath = workflowId ? `/w/${workflowId}/outputs` : "/outputs";
  const backLabel = workflowId ? workflow.name : t("Outputs");

  const itemTitle = getItemTitle(currentItem);

  const displayCounter = hasSession
    ? `${sessionPosition} ${t("of")} ${sessionTotal}`
    : null;

  return (
    <div className="flex items-center w-full bg-white shadow z-10 h-16 flex-shrink-0">
      <div className="flex items-center flex-1 px-4 min-w-0">
        <div className="flex items-center gap-1 text-sm font-gilroy font-semibold flex-shrink-0">
          <button onClick={() => history.push(backPath)} className="flex items-center gap-1 text-gray-400 hover:text-blue hover:underline">
            <ChevronLeftIcon className="h-5 w-5" />
            {backLabel}
          </button>
          <span className="text-gray-300">/</span>
          <span className="text-gray-600 truncate max-w-[250px]">
            {itemTitle}
          </span>
        </div>

        {/* Action required flag + navigation counter + arrows */}
        <div className="flex items-center gap-4 flex-shrink-0 ml-auto">
          <div className="flex items-center gap-4">
            {isActionRequired && (
              <span className="font-gilroy text-sm font-semibold text-blue whitespace-nowrap">
                {t("Action Required")}
              </span>
            )}
            {displayCounter && (
              <span className="font-gilroy text-sm font-semibold text-gray-600 whitespace-nowrap tabular-nums min-w-10 text-right">
                {displayCounter}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className={`p-0.5 rounded hover:bg-gray-100 ${!prevItemId ? "opacity-20 cursor-not-allowed" : ""}`}
              disabled={!prevItemId}
              onClick={() => prevItemId && onNavigate(prevItemId)}
              aria-label={t("Previous item")}
            >
              <ChevronLeftIcon className="w-6 h-6 text-gray-600" />
            </button>
            <button
              type="button"
              className={`p-0.5 rounded hover:bg-gray-100 ${!nextItemId ? "opacity-20 cursor-not-allowed" : ""}`}
              disabled={!nextItemId}
              onClick={() => nextItemId && onNavigate(nextItemId)}
              aria-label={t("Next item")}
            >
              <ChevronRightIcon className="w-6 h-6 text-gray-600" />
            </button>
          </div>
          <UsagePanel className="w-[328px]" />
        </div>
      </div>
    </div>
  );
}
